import React, { useEffect, useState } from 'react';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import { fetchTransactionByTxid, fetchInputAddresses } from '../services/apiService';
import './Txid.css';
import {
  FaKey,
  FaClock,
  FaHashtag,
  FaCogs,
  FaCoins,
  FaInfoCircle,
  FaList,
  FaChevronDown,
  FaChevronUp,
  FaArrowLeft,
  FaArrowRight,
} from 'react-icons/fa';

const TxidPage = () => {
  const { txid } = useParams();
  const [transactionData, setTransactionData] = useState(null);
  const [inputAddresses, setInputAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 5;

  useEffect(() => {
    if (txid) {
      fetchTransactionByTxid(txid).then((data) => {
        setTransactionData(data);
        setLoading(false);
      });

      fetchInputAddresses(txid).then((data) => {
        setInputAddresses(data);
      });
    }
  }, [txid]);

  if (loading) return <p className="text-center">Loading transaction data...</p>;
  if (!transactionData) return <p className="text-center">Transaction not found.</p>;

  const { transaction } = transactionData;

  const paginatedVin = transaction.vin.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const totalPages = Math.ceil(transaction.vin.length / rowsPerPage);

  const isCoinbase = transaction.vin.some(input => input.coinbase);

  const renderTooltip = (props) => (
    <Tooltip {...props}>{transaction.txid}</Tooltip>
); 
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <h1 className="text-3xl font-bold mb-6 flex items-center">
        <FaHashtag className="mr-2 text-blue-500" /> Transaction Details
      </h1>

      <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
      <p>
          <FaKey className="inline mr-2 text-gray-500" />
          <strong>Transaction ID:</strong>{" "}
          <OverlayTrigger placement="top" overlay={renderTooltip}>
              <span className="cursor-pointer text-blue-500 hover:underline">
                  {transaction.txid.slice(0, 4)}...{transaction.txid.slice(-4)}
              </span>
          </OverlayTrigger>
      </p>
        <p>
          <FaClock className="inline mr-2 text-gray-500" />
          <strong>Block Time:</strong> {new Date(transaction.block_time).toLocaleString()}
        </p>
        <p>
          <FaCogs className="inline mr-2 text-gray-500" />
          <strong>Block Height:</strong> {transaction.block_height}
        </p>
        <p>
          <strong>Block Hash:</strong>{' '}
          <Link to={`/block-hash/${transaction.block_hash}`} className="text-blue-500 hover:underline">
          <span className="cursor-pointer text-blue-500 hover:underline">
            {transaction.block_hash.slice(0, 4)}...{transaction.block_hash.slice(-4)}
          </span>
          </Link>
        </p>

        <p>
          <strong>Version:</strong> {transaction.version}
        </p>
        <p>
          <strong>Type:</strong> {transaction.type}
        </p>
        <p>
          <strong>Size:</strong> {transaction.size} bytes
        </p>
      </div>

      {/* Inputs (Vin) */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <FaList className="mr-2 text-yellow-500" /> Inputs (Vin)
        </h2>
        {isCoinbase ? (
          <div className="bg-green-100 text-green-800 px-4 py-2 rounded mb-6">
            <FaCoins className="inline mr-2" />
            This is a <strong>Coinbase</strong> transaction, meaning it generates new coins as part of the block reward.
          </div>
        ) : (
          <>
            <button
              className="bg-blue-500 text-white px-4 py-2 rounded flex items-center"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? <FaChevronUp className="mr-2" /> : <FaChevronDown className="mr-2" />}
              {isExpanded ? 'Collapse' : 'Expand'}
            </button>
            <p className="text-sm text-gray-600 mt-2 flex items-center">
              <FaInfoCircle className="inline mr-2" />
              Expand this table to trace previous transactions.
            </p>

            {isExpanded && (
              <>
                <div className="overflow-x-auto mt-4">
                  <table className="table-auto w-full text-left bg-gray-100 shadow-md rounded mb-4">
                    <thead className="bg-gray-200">
                      <tr>
                        <th className="p-3 border-b">Previous TxID</th>
                        <th className="p-3 border-b">Vout</th>
                        <th className="p-3 border-b">Sequence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedVin.map((input, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="p-3 border-b">
                            {input.previous_txid ? (
                              <Link
                                to={`/txid/${input.previous_txid}`}
                                className="text-blue-500 hover:underline break-all"
                              >
                                {input.previous_txid}
                              </Link>
                            ) : (
                              'N/A'
                            )}
                          </td>
                          <td className="p-3 border-b">
                            {input.vout !== null ? input.vout : 'N/A'}
                          </td>
                          <td className="p-3 border-b">
                            {input.sequence}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex justify-between items-center mt-2">
                  <button
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                  >
                    <FaArrowLeft className="mr-2 inline" /> Previous
                  </button>
                  <p className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                  </p>
                  <button
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded disabled:opacity-50"
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                  >
                    Next <FaArrowRight className="ml-2 inline" />
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Input Addresses & Outputs */}
      <div className="flex flex-col lg:flex-row justify-between space-y-6 lg:space-y-0 lg:space-x-4 mb-6">
        {/* Input Addresses */}
        <div className="bg-white shadow-lg rounded-lg p-6 flex-1">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaList className="mr-2 text-blue-500" /> Input Addresses
          </h2>
          <div className="overflow-x-auto">
            {inputAddresses.length > 0 ? (
              <table className="table-auto w-full text-left">
                <thead className="bg-gray-200">
                  <tr>
                    <th className="p-3 border-b">Address</th>
                    <th className="p-3 border-b">Value</th>
                  </tr>
                </thead>
                <tbody>
                {inputAddresses.map((input, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="p-3 border-b break-all">
                      <Link 
                        to={`/address/${input.address}`} 
                        className="text-blue-500 hover:underline"
                      >
                        {input.address}
                      </Link>
                    </td>
                    <td className="p-3 border-b">{input.value}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            ) : (
              <p className="text-gray-500">No input addresses available.</p>
            )}
          </div>
        </div>

        {/* Outputs */}
        <div className="bg-white shadow-lg rounded-lg p-6 flex-1">
          <h2 className="text-xl font-semibold mb-4 flex items-center">
            <FaList className="mr-2 text-green-500" /> Outputs (Vout)
          </h2>
          <div className="overflow-x-auto">
            <table className="table-auto w-full text-left">
              <thead className="bg-gray-200">
                <tr>
                  <th className="p-3 border-b">Index</th>
                  <th className="p-3 border-b">Value (MENEL)</th>
                  <th className="p-3 border-b">Address</th>
                </tr>
              </thead>
              <tbody>
                {transaction.vout.map((output, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="p-3 border-b">{output.n}</td>
                    <td className="p-3 border-b">{output.value}</td>
                    <td className="p-3 border-b break-all">
                      {output.scriptPubKey.addresses ? (
                        output.scriptPubKey.addresses.map((address, i) => (
                          <span key={i}>
                            <Link 
                              to={`/address/${address}`} 
                              className="text-green-500 hover:underline"
                            >
                              {address}
                            </Link>
                            {i < output.scriptPubKey.addresses.length - 1 && ', '}
                          </span>
                        ))
                      ) : (
                        'N/A'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TxidPage;
